import { Textarea } from "src/sudokit/components/Textarea";
import { SudoButton } from "src/sudokit/components/SudoButton";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { forwardRef } from "react";
// @ts-ignore
import styled from "styled-components/macro";
import WorldbuildingGenerationAnimation from "./WorldbuildingGenerationAnimation";

interface EntryRoleOption {
  label: string;
  value: string;
}

interface GenerateElementFormProps {
  showGenerateElementForm: boolean;
  setShowGenerateElementForm: (show: boolean) => void;
  guidance: string;
  guidanceRoleOptions: EntryRoleOption[];
  guidanceRoleOption: EntryRoleOption | null;
  handleGuidanceRoleChange: (value: EntryRoleOption | null) => void;
  setGuidance: (guidance: string) => void;
  handleGenerateSubmitted: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleCancel: () => void;
  generatingElement: boolean;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const GenerateElementForm = forwardRef(
  (
    {
      showGenerateElementForm, //
      guidance,
      guidanceRoleOptions,
      guidanceRoleOption,
      handleGuidanceRoleChange,
      setGuidance,
      handleGenerateSubmitted,
      handleCancel,
      generatingElement,
      onKeyDown,
    }: GenerateElementFormProps,
    ref
  ) => {
    return (
      <GenerateElementFormContainer
        ref={ref}
        $showGenerateElementForm={showGenerateElementForm}
        aria-hidden={!showGenerateElementForm}
        {...(!showGenerateElementForm && { inert: "" })}
      >
        <div className="generate-element-form-header">
          <p className="">Generate Element</p>
          {!generatingElement && (
            <MenuSelect
              label="Role"
              onChange={(value) => {
                const option = guidanceRoleOptions.find((option) => option.value === value);
                if (option) {
                  handleGuidanceRoleChange(option);
                }
              }}
              options={guidanceRoleOptions}
              value={guidanceRoleOption?.value}
              placeholder="Select a type"
            />
          )}
        </div>
        {generatingElement && (
          <>
            {/* This is a placeholder and will be replaced with an animation */}
            <div className="generating-character-form-loading">
              <WorldbuildingGenerationAnimation />
            </div>
            <div className="generate-element-form-footer">
              <SudoButton
                variant="tertiary"
                goButton={false}
                state="loading"
                loadingPercentage={0}
                className="untouchable-loading-button"
              >
                Generating
              </SudoButton>
            </div>
          </>
        )}
        {!generatingElement && (
          <>
            <Textarea
              placeholder="Write custom instructions. Example: A cozy home library for my protagonist"
              mode="default"
              className="text-base"
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <div className="generate-element-form-footer">
              <SudoButton
                variant="secondaryGrey"
                onClick={handleCancel}
              >
                Cancel
              </SudoButton>
              <SudoButton
                variant="primary"
                goButton
                onClick={handleGenerateSubmitted}
              >
                Go
              </SudoButton>
            </div>
          </>
        )}
      </GenerateElementFormContainer>
    );
  }
);

export default GenerateElementForm;

type GenerateElementFormContainerProps = {
  $showGenerateElementForm: boolean;
};

const GenerateElementFormContainer = styled.div<GenerateElementFormContainerProps>`
  height: ${(props: GenerateElementFormContainerProps) => (props.$showGenerateElementForm ? "auto" : 0)};
  padding: ${(props: GenerateElementFormContainerProps) => (props.$showGenerateElementForm ? "10px 14px" : 0)};
  margin: ${(props: GenerateElementFormContainerProps) => (props.$showGenerateElementForm ? "0px 16px" : 0)};
  overflow: hidden;
  background-color: var(--menu-color);
  border-radius: 20px;
  box-shadow: ${(props: GenerateElementFormContainerProps) => (props.$showGenerateElementForm ? "var(--tiny-drop-shadow-head-on)" : "none")};
  margin-bottom: 10px;
  position: relative;
  z-index: 1;

  p {
    color: var(--grey-0);
    font-family: "Inter", sans-serif;
    font-weight: 500;
    background: transparent;
    border: none;
    padding-left: 0;
    padding-right: 0;
    font-size: 16px;
  }

  .generate-element-form-header {
    display: flex;
    padding-bottom: 10px;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;
  }

  .untouchable-loading-button {
    &:hover {
      background: transparent !important;
    }

    &:hover::before,
    &:focus-visible::before {
      background: transparent !important;
      border-radius: 0 !important;
    }
  }

  .generating-character-form-loading {
    margin-bottom: -65px;
    margin-left: -14px;
    margin-right: -14px;
  }

  .generate-element-form-footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }
`;
